import React, { useContext, useState, useRef } from 'react';

import { GenericFileData } from '@api/v4/assets/assetTypes';

import {
  ActionTypes,
  AssetDetailsDispatch,
} from '@components/asset/modal/tabs/edit/EditTabTypes';

import assetModalContext from '@components/asset/modal/tabs/edit/asset_modal_context';

import RadioButton from '@components/common/radio_button/main';

// these colors are treated differently than hex inputs
// and format matters
// we use a map so we can use them as constants too
const ReservedColors = {
  Default: "default",
  White: "white"
};

interface AssetBackgroundColorScopedContext {
  state: {
    editState: {
      genericFileData: GenericFileData;
    };
  };
  dispatch: AssetDetailsDispatch;
}

const AssetBackgroundColor = (): React.ReactElement => {
  // a try at keeping things immutable.
  Object.freeze(ReservedColors);

  const { state, dispatch }: AssetBackgroundColorScopedContext = useContext(assetModalContext);
  /* eslint-disable @typescript-eslint/camelcase */
  const { genericFileData } = state.editState;
  const { asset_background_color } = genericFileData;

  const shouldDefaultBeChecked = !asset_background_color || (asset_background_color === ReservedColors.Default);
  const isDefaultChecked = shouldDefaultBeChecked ? ReservedColors.Default : '';

  const isWhiteChecked = asset_background_color === ReservedColors.White ? ReservedColors.White : '';

  const isHexChecked = asset_background_color?.substring(0, 1) === '#' ? '#' : '';

  const ReservedColorValues = Object.keys(ReservedColors).map((color) => ReservedColors[color]);

  const defaultHexValue = (!asset_background_color || ReservedColorValues.includes(asset_background_color)) ? '' : asset_background_color;

  const [hexValue, setHexValue] = useState(defaultHexValue);

  const inputEl = useRef(null);

  const updateBackgroundColor = (value: string, isHexInput: boolean): void => {
    let trimmedValue = value.trim();

    if (isHexInput) {
      if (!trimmedValue.startsWith('#')) trimmedValue = '#'.concat(trimmedValue);
      setHexValue(trimmedValue);
    } else {
      // if we choose another option, clear hex input
      setHexValue('');
    }

    const payload = {
      genericFileData: {
        ...genericFileData,
        asset_background_color: trimmedValue
      }
    };
    dispatch({ type: ActionTypes.UpdateAsset, payload });
  };

  return (
    <div className="asset-advanced__feature asset-background-color">
      <span>{bfTranslate("Asset Background Color")}</span>
      <div className="radio-button-container">
        <RadioButton
          checkedButton={isDefaultChecked}
          labelCopy={bfTranslate("Default")}
          name="asset-background-color"
          onChangeFunction={(): void => updateBackgroundColor(ReservedColors.Default, false)}
          valueCopy={ReservedColors.Default}
        />
        <span className="default-background-color">({bfTranslate("transparent")})</span>
        <RadioButton
          checkedButton={isWhiteChecked}
          labelCopy={bfTranslate("White")}
          name="asset-background-color"
          onChangeFunction={(): void => updateBackgroundColor(ReservedColors.White, false)}
          valueCopy={ReservedColors.White}
        />
        <>
          <RadioButton
            checkedButton={isHexChecked}
            labelCopy={bfTranslate("Hex")}
            name="asset-background-color"
            onChangeFunction={(): void => { updateBackgroundColor('#', true); inputEl.current.focus(); }}
            valueCopy="#"
          />
          <input
            ref={inputEl}
            className="advanced-input"
            onFocus={(): void => updateBackgroundColor('#', true)}
            onChange={(e): void => updateBackgroundColor(e.currentTarget.value, true)}
            value={hexValue}
          />
        </>
      </div>
    </div>
  );
};

export default AssetBackgroundColor;
